import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'
import { PHENO_CODES } from './usePhenomenaData'

// Denní jevy pro skupinu stanic — paralelní phenomena_day fetch přes STANICE (obdoba
// usePhenomenaDayData, ale ve smyčce přes ghIds). Viz
// doc/plans/station-group-monthly-yearly-phenomena.md sekce 5c.
//
// Jeden fetch obsluhuje obě záložky produktu grp-jevy:
// - dayMap:   Map<gh_id, Map<"YYYY-MM-DD", Set<phenoCode>>>  — záložka Detail + rozbalený řádek
// - countMap: Map<gh_id, Map<phenoCode, number>>             — záložka Přehled (počet dní s jevem)
export function useGroupPhenomenaData(ghIds, year, month, enabled = true) {
  const mm = String(month).padStart(2, '0')
  const from = `${year}-${mm}`

  const queryKey = ['groupPhenomenaData', [...ghIds].sort(), year, month]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const empty = { data: [], to_fill: [] }
      const responses = await fetchTsIds(
        ghIds,
        ghId => api.get(`/phenomena_day?gh_id=${ghId}&from=${from}&to=${from}`).catch(() => empty),
      )
      const result = Object.fromEntries(ghIds.map((ghId, i) => [ghId, responses[i]?.data ?? []]))
      const toFill = responses.flatMap(r => r?.to_fill ?? [])
      return { result, toFill }
    },
    enabled: enabled && ghIds.length > 0 && !!year && !!month,
    staleTime: 5 * 60 * 1000,
  })

  // Živé = zobrazený měsíc je aktuální (server dotahuje jen k včerejšku,
  // viz doc/plans/phenomena-no-today.md).
  const now = new Date()
  const live = Number(year) === now.getUTCFullYear() && Number(month) === now.getUTCMonth() + 1
  useToFill(queryKey, data, (result, resp, item) => {
    const gh = item.params?.gh_id
    if (!gh || !(gh in result)) return result
    return { ...result, [gh]: resp.data }
  }, { gran: 'DLY', live })

  const dayMap = new Map()
  const countMap = new Map()

  if (data?.result) {
    ghIds.forEach(ghId => {
      const perDay = new Map()
      const rows = Array.isArray(data.result[ghId]) ? data.result[ghId] : []
      rows.forEach(row => {
        const key = row.dt ?? row.date
        if (!key || !row.phenomenon) return
        const codes = perDay.get(key) ?? new Set()
        codes.add(row.phenomenon)
        perDay.set(key, codes)
      })
      dayMap.set(ghId, perDay)

      const counts = new Map(PHENO_CODES.map(c => [c, 0]))
      for (const codes of perDay.values()) {
        for (const c of codes) {
          if (counts.has(c)) counts.set(c, counts.get(c) + 1)
        }
      }
      countMap.set(ghId, counts)
    })
  }

  return { dayMap, countMap, isLoading, isError }
}
